import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";
import {
  formatMessage,
  formatText,
  Locale,
  MessageDescriptor,
  MessageKey,
  TranslatableText,
} from "backend/src/common/i18n";
import {
  getCurrentLocale,
  setCurrentLocale,
  subscribeLocale,
} from "./localeStore";

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, values?: MessageDescriptor["values"]) => string;
  formatDescriptor: (value: TranslatableText) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

const useLocaleValue = (): Locale => {
  return useSyncExternalStore(
    subscribeLocale,
    getCurrentLocale,
    getCurrentLocale,
  );
};

export const I18nProvider = ({ children }: PropsWithChildren<{}>) => {
  const locale = useLocaleValue();

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => {
    return {
      locale,
      setLocale: setCurrentLocale,
      t: (key, values) => formatMessage(locale, key, values),
      formatDescriptor: (text) => formatText(locale, text),
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextValue => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
};

export const useT = () => useI18n().t;
